import { apiClient } from '@/core/api/client';
import type { MatchedSpecialtyApi } from '../navigator-matching.types';
import { buildSelectionPayload } from '../selection';

// The matchmaking pipeline is kicked fire-and-forget by GET /care-options and
// runs server-side for several seconds. We poll /find-providers/status until
// the backend reports READY rather than relying on SSE (long-lived sockets +
// a premature-ready race that showed "0 providers" until a refresh). The ~90s
// cap mirrors the backend pipeline's own ceiling.
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 30;
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export type MatchingPipelineCallbacks = {
  /** /care-options resolved — reports the care stage's specialty count. */
  onCareOptions?: (totalSpecialties: number) => void;
  /** Categories snapshot — fired on mid-poll refreshes and the terminal load. */
  onCategories: (categories: MatchedSpecialtyApi[]) => void;
  /** Poll reported READY; the terminal load + care-plan build are about to run. */
  onReadySignal?: () => void | Promise<void>;
};

export type MatchingPipelineResult = {
  ok: boolean;
  categories: MatchedSpecialtyApi[];
};

/**
 * Headless matchmaking pipeline, ported from the pre-store useNavigatorMatching:
 * kick /care-options, poll /find-providers/status to READY (90s cap), load the
 * matched specialties, and persist a care plan with the top match per specialty.
 *
 * Exactly one run per session lives in the generation store, so the old
 * StrictMode/sequence guards are no longer needed here — calls are sequential.
 */
export const runMatchingPipeline = async (
  sessionId: string,
  callbacks: MatchingPipelineCallbacks,
): Promise<MatchingPipelineResult> => {
  let planBuilt = false;

  const buildPlan = async (loaded: MatchedSpecialtyApi[]) => {
    if (planBuilt) return;
    // Auto-select = AI top match (rank 1) — unless the backend already carries
    // a user_selected flag (reload after a swap), which must be preserved.
    const selectedProviders = buildSelectionPayload(loaded);
    // Latch the guard only once there is something to persist, so an early
    // READY with no providers doesn't permanently block a later populated build.
    if (selectedProviders.length === 0) return;
    planBuilt = true;
    try {
      await apiClient.post('/find-providers/care-plan', {
        session_id: sessionId,
        selected_providers: selectedProviders,
      });
    } catch {
      // Best-effort persistence — results still render from the live matches.
    }
  };

  const fetchSpecialties = async (): Promise<MatchedSpecialtyApi[]> => {
    const { data } = await apiClient.get<MatchedSpecialtyApi[]>('/find-providers/specialties', {
      params: { session_id: sessionId },
    });
    return data ?? [];
  };

  const terminalLoad = async (): Promise<MatchingPipelineResult> => {
    await callbacks.onReadySignal?.();
    try {
      const loaded = await fetchSpecialties();
      callbacks.onCategories(loaded);
      await buildPlan(loaded);
      return { ok: true, categories: loaded };
    } catch {
      // Only the terminal load surfaces an error — transient mid-poll blips
      // must not flip the UI out of the building state.
      return { ok: false, categories: [] };
    }
  };

  // Kick off the matchmaking run. GET /care-options resolves the care stage and
  // fire-and-forget persists + runs the matchmaking searches that
  // /find-providers/status reports on. Idempotent: persistence no-ops when the
  // searches already exist. Its body is the full ordered specialty list — the
  // earliest real "total" signal for the loading page's k-of-N counter.
  try {
    const { data } = await apiClient.get<unknown[]>('/care-options', {
      params: { session_id: sessionId },
    });
    callbacks.onCareOptions?.(Array.isArray(data) ? data.length : 0);
  } catch {
    // Care stage may be unresolved — the status poll still reports and caps.
  }

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    try {
      const { data } = await apiClient.get<{ status: string }>('/find-providers/status', {
        params: { session_id: sessionId },
      });
      if (data.status === 'READY') {
        return await terminalLoad();
      }
      // Still processing — refresh categories behind the skeleton/loading page.
      try {
        callbacks.onCategories(await fetchSpecialties());
      } catch {
        // Transient mid-poll refresh failure — keep polling.
      }
    } catch {
      // Transient — retry on the next tick.
    }
    await delay(POLL_INTERVAL_MS);
  }
  // ponytail: the 90s cap is the ceiling — render whatever is available so the
  // flow isn't stuck building. Upgrade path (major pass): a retry CTA and/or
  // moving the pipeline kickoff to a job queue.
  return terminalLoad();
};
